import { useState } from 'react';

export default function RatingScreen({ agent, duration, onDone }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleSubmit = () => {
    if (rating === 0) return;
    setSubmitted(true);
    setTimeout(() => onDone(), 2000);
  };

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.successCard}>
          <div style={styles.successEmoji}>🎉</div>
          <div style={styles.successTitle}>Session Complete!</div>
          <div style={styles.successSub}>
            Thank you for practicing German today.
          </div>
          <div style={styles.successStats}>
            <div style={styles.successStat}>
              <div style={styles.successStatVal}>{fmt(duration)}</div>
              <div style={styles.successStatLabel}>Duration</div>
            </div>
            <div style={styles.successDivider}></div>
            <div style={styles.successStat}>
              <div style={styles.successStatVal}>{'⭐'.repeat(rating)}</div>
              <div style={styles.successStatLabel}>Your rating</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Session summary */}
        <div style={styles.summary}>
          <div style={styles.avatar}>{agent.avatar}</div>
          <div style={styles.summaryInfo}>
            <div style={styles.summaryName}>{agent.codename}</div>
            <div style={styles.summarySpec}>{agent.specialty}</div>
          </div>
          <div style={styles.duration}>{fmt(duration)}</div>
        </div>

        <div style={styles.divider}></div>

        {/* Rating */}
        <div style={styles.ratingTitle}>
          How was your session?
        </div>
        <div style={styles.starsRow}>
          {[1, 2, 3, 4, 5].map(star => (
            <span
              key={star}
              style={{
                ...styles.star,
                color: star <= (hover || rating) ? '#f59e0b' : '#ffffff20',
              }}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
            >
              ★
            </span>
          ))}
        </div>
        <div style={styles.ratingLabel}>
          {rating === 0 && 'Tap to rate'}
          {rating === 1 && 'Poor'}
          {rating === 2 && 'Fair'}
          {rating === 3 && 'Good'}
          {rating === 4 && 'Very Good'}
          {rating === 5 && 'Excellent! 🎉'}
        </div>

        {/* Feedback */}
        <textarea
          style={styles.textarea}
          placeholder="Any feedback? (optional)"
          value={feedback}
          onChange={e => setFeedback(e.target.value)}
          rows={3}
        />

        {/* Submit */}
        <button
          style={{
            ...styles.submitBtn,
            opacity: rating === 0 ? 0.4 : 1,
          }}
          onClick={handleSubmit}
          disabled={rating === 0}
        >
          Submit Rating
        </button>

        <button style={styles.skipBtn} onClick={() => onDone()}>
          Skip
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0f0f1a',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: 'sans-serif',
  },
  card: {
    backgroundColor: '#1e1e30',
    borderRadius: '24px',
    padding: '28px',
    width: '100%',
    maxWidth: '400px',
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  avatar: {
    width: '50px',
    height: '50px',
    borderRadius: '14px',
    backgroundColor: '#2d1b69',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    flexShrink: 0,
  },
  summaryInfo: { flex: 1 },
  summaryName: {
    color: '#ffffff',
    fontWeight: '800',
    fontSize: '15px',
  },
  summarySpec: {
    color: '#6366f1',
    fontSize: '12px',
    fontWeight: '700',
  },
  duration: {
    color: '#ffffff60',
    fontSize: '14px',
    fontWeight: '700',
  },
  divider: {
    height: '1px',
    backgroundColor: '#ffffff10',
    marginBottom: '20px',
  },
  ratingTitle: {
    color: '#ffffff',
    fontWeight: '800',
    fontSize: '18px',
    textAlign: 'center',
    marginBottom: '16px',
  },
  starsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  star: {
    fontSize: '48px',
    cursor: 'pointer',
    transition: 'color 0.1s',
    userSelect: 'none',
  },
  ratingLabel: {
    color: '#ffffff60',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '20px',
    height: '20px',
  },
  textarea: {
    width: '100%',
    backgroundColor: '#0f0f1a',
    border: '1px solid #ffffff10',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '14px',
    padding: '12px',
    resize: 'none',
    outline: 'none',
    marginBottom: '16px',
    boxSizing: 'border-box',
    fontFamily: 'sans-serif',
  },
  submitBtn: {
    width: '100%',
    backgroundColor: '#6366f1',
    border: 'none',
    borderRadius: '14px',
    padding: '16px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '800',
    cursor: 'pointer',
    marginBottom: '10px',
  },
  skipBtn: {
    width: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ffffff40',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '8px',
  },
  successCard: {
    backgroundColor: '#1e1e30',
    borderRadius: '24px',
    padding: '40px 28px',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  successEmoji: { fontSize: '60px', marginBottom: '16px' },
  successTitle: {
    color: '#ffffff',
    fontWeight: '900',
    fontSize: '24px',
    marginBottom: '8px',
  },
  successSub: {
    color: '#ffffff60',
    fontSize: '14px',
    marginBottom: '24px',
  },
  successStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    backgroundColor: '#ffffff08',
    borderRadius: '16px',
    padding: '16px',
  },
  successStat: { textAlign: 'center' },
  successStatVal: {
    color: '#ffffff',
    fontWeight: '800',
    fontSize: '20px',
  },
  successStatLabel: {
    color: '#ffffff50',
    fontSize: '12px',
    marginTop: '4px',
  },
  successDivider: {
    width: '1px',
    backgroundColor: '#ffffff15',
  },
};